import { useEffect, useState } from "react";

import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import {
  Bath,
  Bed,
  Building,
  Calendar,
  MapPin,
  MapPinned,
  Ruler,
  User,
} from "lucide-react";

import NewMap from "../../common/NewMap";
import {
  formatTitleOwnerNamesFromJson,
  getObjLength,
  propertyStreetLine,
} from "../../helpers/utils";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS } from "../../helpers/constants";

const PropertyInfoBox = ({
  handlePropertyConfirmYes,
  handlePropertyConfirmNo,
  isPropertyConfirmed,
  onNextStep,
  onSearchAgain,
  category,
  errorMessage,
}: {
  handlePropertyConfirmYes: () => void;
  handlePropertyConfirmNo: () => void;
  isPropertyConfirmed: boolean | null;
  onNextStep?: () => void;
  onSearchAgain?: () => void;
  category?: string;
  errorMessage?: string;
}) => {
  const isMonitoringCategory =
    category === "enhanced_monitoring" || category === "basic_monitoring";
  const [selectedProeprtyData, setSelectedProeprtyData] = useState(null);

  const { selectedProperty, propertyQuestion } = useSelector(
    (state: any) => state.signUpUser,
  );

  const {
    selectedProperty: selectedPropertyDetails,
    propertyQuestion: propertyDetails,
  } = useSelector((state: any) => state.properties);

  const activePropertyQuestion = getObjLength(propertyDetails)
    ? propertyDetails
    : propertyQuestion;

  useEffect(() => {
    if (getObjLength(selectedProperty)) {
      setSelectedProeprtyData(selectedProperty);
    }
  }, [propertyQuestion, selectedProperty]);

  useEffect(() => {
    // Prefer properties-store selection (admin/SP add-property) even when the
    // create/questions call failed — we still need the card + error message.
    if (getObjLength(selectedPropertyDetails)) {
      setSelectedProeprtyData(selectedPropertyDetails);
    }
  }, [propertyDetails, selectedPropertyDetails]);

  useEffect(() => {
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const showNextActions =
    isPropertyConfirmed === true &&
    (activePropertyQuestion?.success ||
      (isMonitoringCategory && isPropertyConfirmed));

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-gradient-to-br from-secondary via-secondary to-cyan-500 rounded-2xl p-6 md:p-8 text-white shadow-xl"
    >
      {/* Property Image */}
      <div
        className="relative overflow-hidden mb-6 shadow-lg rounded-lg mt-2"
        style={{ height: "225px", position: "relative" }}
      >
        <NewMap
          className="w-full h-full"
          zoom={15}
          center={{
            lat: parseFloat(selectedProeprtyData?.latitude), //41.363114
            lng: parseFloat(selectedProeprtyData?.longitude), //-74.186442
          }}
          coordinates={[]}
          streetView={true}
        />
      </div>

      {/* Property Address */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-1">
          {propertyStreetLine(
            selectedProeprtyData?.address,
            selectedProeprtyData?.city,
            selectedProeprtyData?.state,
            selectedProeprtyData?.zip,
          ) || "-"}
        </h2>
        <p className="text-white/80 text-sm">
          {selectedProeprtyData?.city || "-"},{" "}
          {selectedProeprtyData?.state || "-"}
        </p>
      </div>

      {/* Property Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <MapPinned className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">APN</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.json_response?.sa_parcel_nbr_primary || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Owner</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {formatTitleOwnerNamesFromJson(selectedProeprtyData) || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Building className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Type</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.type || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Bed className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Beds</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.bed_rooms ||
              selectedProeprtyData?.beds_rooms ||
              "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Bath className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Baths</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.baths || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Ruler className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Sqft</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.sqft?.toLocaleString() || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">Year Built</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.year_built || "-"}
          </p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-white/70 shrink-0" />
            <span className="text-xs text-white/70">County</span>
          </div>
          <p className="font-medium text-sm break-all overflow-hidden">
            {selectedProeprtyData?.county || "-"}
          </p>
        </div>
      </div>

      {errorMessage ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-lg bg-white p-4 mb-6 text-center space-y-3"
        >
          <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
          {onSearchAgain && (
            <Button
              type="button"
              onClick={onSearchAgain}
              className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
            >
              SEARCH AGAIN
            </Button>
          )}
        </motion.div>
      ) : (
        /* Is this your property question */
        <div className="bg-gradient-to-b from-green-500 to-green-600 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3 text-center">
            Is this your property?
          </h3>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={handlePropertyConfirmYes}
              variant={isPropertyConfirmed === true ? "default" : "outline"}
              className={`px-8 ${
                isPropertyConfirmed === true
                  ? "bg-green-500 hover:bg-green-600 text-white border-green-500"
                  : "bg-white/20 hover:bg-white/30 text-white border-white/40"
              }`}
            >
              Yes
            </Button>
            <Button
              onClick={handlePropertyConfirmNo}
              variant={isPropertyConfirmed === false ? "default" : "outline"}
              className={`px-8 ${
                isPropertyConfirmed === false
                  ? "bg-red-500 hover:bg-red-600 text-white border-red-500"
                  : "bg-white/20 hover:bg-white/30 text-white border-white/40"
              }`}
            >
              No
            </Button>
          </div>

          {isPropertyConfirmed === false && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mt-3 space-y-3"
            >
              <p className="text-yellow-200 text-sm">
                Please search again with a different address or parcel number.
              </p>
              {onSearchAgain && (
                <Button
                  type="button"
                  onClick={onSearchAgain}
                  className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
                >
                  SEARCH AGAIN
                </Button>
              )}
            </motion.div>
          )}

          {showNextActions && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 space-y-4"
            >
              <div className="flex flex-wrap gap-3 justify-center">
                {onSearchAgain && (
                  <Button
                    type="button"
                    onClick={onSearchAgain}
                    variant="outline"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/40"
                  >
                    SEARCH AGAIN
                  </Button>
                )}
                {onNextStep && (
                  <Button
                    type="button"
                    onClick={onNextStep}
                    className="bg-white text-secondary hover:bg-white/90 font-medium px-10"
                  >
                    NEXT
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Trust Badges */}
      <div className="flex justify-center gap-4 pt-4 border-t border-white/20">
        <img
          src={oneMillionBadge}
          alt="$1M Protection"
          className="h-12 object-contain"
        />
        <img
          src={bbbBadge}
          alt="BBB Accredited"
          className="h-12 object-contain"
        />
        <img
          src={soc2Badge}
          alt="SOC2 Certified"
          className="h-12 object-contain"
        />
      </div>
    </motion.div>
  );
};

export default PropertyInfoBox;
